import {SetExternalEditorUrlErrorAction} from "@data-force/app/interfaces/store/actions/editor";
import {EXTERNAL_EDITOR_URL_ERROR} from "@data-force/app/store/actions/availableActions";
import {ContentType, FilterType, SortDirection} from "../../enums/enums";
import {
    ClearJobListDataAction,
    DestroyConfirmClaimDialogAction,
    DestroyDetailsDrawerAction,
    OpenJobEditorAction,
    ResetActivityNotificationAction,
    SetActiveJobTypeAction,
    SetChromeExtensionMissingVisibleAction,
    SetExternalJobUrlAction,
    SetFiltersDrawerVisibilityAction,
    SetFilterTypeAction,
    SetJobListAction,
    SetJobListCursorAction,
    SetJobPhaseWorkflowDefinitionsAction,
    SetJobSearchAction,
    SetJobTestTypeAction,
    SetPhaseConfigListAction,
    SetSelectedFiltersAction,
    SetSelectedJobAction,
    SetSortDirectionAction,
    SetUnsupportedDomainVisibleAction,
    ShowActivityNotificationAction,
    ShowConfirmClaimDialogAction,
    ShowDetailsDrawerAction,
    ToggleInitiateWorkDialogAction,
    UpdateJobListAction,
} from "../../interfaces/store/actions/job";
import {JobState} from "../../interfaces/store/reducers/jobState";
import {DrawerTabs} from "../../utils/constants";
import {
    CLEAR_JOB_LIST_DATA,
    DESTROY_CONFIRM_CLAIM_DIALOG,
    DESTROY_DETAILS_DRAWER,
    OPEN_JOB_EDITOR,
    RESET_ACTIVITY_NOTIFICATION,
    SET_ACTIVE_JOB_TYPE,
    SET_CHROME_EXTENSION_MISSING_VISIBLE,
    SET_EXTERNAL_JOB_URL,
    SET_FILTER_TYPE,
    SET_FILTERS_DRAWER_VISIBILITY,
    SET_JOB_LIST,
    SET_JOB_LIST_CURSOR,
    SET_JOB_PHASE_WORKFLOW_DEFINITIONS,
    SET_JOB_SEARCH,
    SET_JOB_TEST_TYPE,
    SET_PHASE_CONFIG_LIST,
    SET_SELECTED_FILTERS,
    SET_SELECTED_JOB,
    SET_SORT_DIRECTION,
    SET_UNSUPPORTED_DOMAIN_VISIBLE,
    SHOW_ACTIVITY_NOTIFICATION,
    SHOW_CONFIRM_CLAIM_DIALOG,
    SHOW_DETAILS_DRAWER,
    TOGGLE_INITIATE_WORK_DIALOG,
    UPDATE_JOB_LIST,
} from "../actions/availableActions";

const initialState: JobState = {
    cursor: null,
    list: [],
    searchValue: "",
    selectedJob: null,
    workflowDefinitions: [],
    phaseConfigList: {},
    activeJobType: ContentType.AVAILABLE,
    filter: FilterType.CLAIMABLE,
    sortDirection: SortDirection.DESC,
    isDetailsDrawerVisible: false,
    isFiltersDrawerVisible: false,
    isConfirmClaimVisible: false,
    initiateWorkDialogVisible: false,
    isTest: false,
    notificationData: null,
    activeDetailsDrawerTab: DrawerTabs.STATS,
    openEditor: false,
    externalJobUrl: null,
    selectedFilters: {
        skills: [],
        types: [],
        statuses: [],
        decoratedStatuses: [],
    },
    externalEditorError: null,
    isChromeExtensionMissingVisible: false,
    isUnsupportedDomainVisible: false,
};

type AvailableActions =
    SetJobListAction
    |SetJobSearchAction
    |SetJobListCursorAction
    |SetPhaseConfigListAction
    |SetActiveJobTypeAction
    |SetFilterTypeAction
    |SetSortDirectionAction
    |ClearJobListDataAction
    |SetSelectedJobAction
    |UpdateJobListAction
    |ShowDetailsDrawerAction
    |ShowConfirmClaimDialogAction
    |DestroyConfirmClaimDialogAction
    |ToggleInitiateWorkDialogAction
    |SetJobPhaseWorkflowDefinitionsAction
    |ShowActivityNotificationAction
    |ResetActivityNotificationAction
    |DestroyDetailsDrawerAction
    |OpenJobEditorAction
    |SetJobTestTypeAction
    |SetSelectedFiltersAction
    |SetExternalJobUrlAction
    |SetFiltersDrawerVisibilityAction
    |SetExternalEditorUrlErrorAction
    |SetChromeExtensionMissingVisibleAction
    |SetUnsupportedDomainVisibleAction;

const jobReducer = (state: JobState = initialState, action: AvailableActions): JobState => {
    switch (action.type) {
        case EXTERNAL_EDITOR_URL_ERROR:
            return {
                ...state,
                externalEditorError: action.payload,
            };
        case SET_JOB_SEARCH:
            return {
                ...state,
                searchValue: action.payload || "",
            };
        case SET_JOB_PHASE_WORKFLOW_DEFINITIONS:
            return {
                ...state,
                workflowDefinitions: action.payload,
            };
        case SET_JOB_LIST:
            return {
                ...state,
                list: action.payload,
            };
        case SET_JOB_LIST_CURSOR:
            return {
                ...state,
                cursor: action.payload,
            };
        case SET_PHASE_CONFIG_LIST:
            return {
                ...state,
                phaseConfigList: action.payload,
            };
        case UPDATE_JOB_LIST:
            return {
                ...state,
                list: [...state.list, ...action.payload],
            };
        case SET_ACTIVE_JOB_TYPE:
            return {
                ...state,
                activeJobType: action.payload,
            };
        case SET_FILTER_TYPE:
            return {
                ...state,
                filter: action.payload,
            };
        case SET_SORT_DIRECTION:
            return {
                ...state,
                sortDirection: action.payload,
            };
        case SET_SELECTED_JOB:
            return {
                ...state,
                selectedJob: action.payload,
            };
        case SHOW_DETAILS_DRAWER:
            return {
                ...state,
                isDetailsDrawerVisible: true,
                activeDetailsDrawerTab: action.payload,
            };
        case SHOW_CONFIRM_CLAIM_DIALOG:
            return {
                ...state,
                isConfirmClaimVisible: true,
                selectedJob: action.payload,
            };
        case DESTROY_CONFIRM_CLAIM_DIALOG:
            return {
                ...state,
                isConfirmClaimVisible: false,
                selectedJob: null,
            };
        case TOGGLE_INITIATE_WORK_DIALOG:
            return {
                ...state,
                initiateWorkDialogVisible: !state.initiateWorkDialogVisible,
                selectedJob: !state.initiateWorkDialogVisible ? action.payload : null,
            };
        case DESTROY_DETAILS_DRAWER:
            return {
                ...state,
                isDetailsDrawerVisible: false,
            };
        case SET_FILTERS_DRAWER_VISIBILITY:
            return {
                ...state,
                isFiltersDrawerVisible: action.payload,
            };
        case SHOW_ACTIVITY_NOTIFICATION:
            return {
                ...state,
                notificationData: action.payload,
            };
        case RESET_ACTIVITY_NOTIFICATION:
            return {
                ...state,
                notificationData: null,
            };
        case OPEN_JOB_EDITOR:
            return {
                ...state,
                openEditor: action.payload,
            };
        case CLEAR_JOB_LIST_DATA:
            return {
                ...state,
                list: [],
                cursor: null,
                selectedJob: null,
                searchValue: "",
                openEditor: false,
                sortDirection: initialState.sortDirection,
                selectedFilters: {
                    skills: [],
                    types: [],
                    statuses: [],
                    decoratedStatuses: [],
                },
            };
        case SET_JOB_TEST_TYPE:
            return {
                ...state,
                isTest: action.payload,
            };
        case SET_SELECTED_FILTERS:
            return {
                ...state,
                selectedFilters: action.payload,
            };
        case SET_EXTERNAL_JOB_URL:
            return {
                ...state,
                externalJobUrl: action.payload,
            };
        case SET_CHROME_EXTENSION_MISSING_VISIBLE:
            return {
                ...state,
                isChromeExtensionMissingVisible: action.payload,
            };
        case SET_UNSUPPORTED_DOMAIN_VISIBLE:
            return {
                ...state,
                isUnsupportedDomainVisible: action.payload,
            };
        default:
            return state;
    }
};

export default jobReducer;
